/**
 * Iter XX.223 — WelcomeBackReveal + MyCricketCounters + billing-CTA click beacon.
 *
 * Two exports:
 *   · WelcomeBackReveal — inline chat card. Founder rule A: waits
 *     for the FIRST user message in this session before rendering.
 *   · MyCricketCounters — factual counters strip; renders in the
 *     bottom-sheet + /me. Feature-flagged (null when off).
 */
import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { fetchCounters, fetchReturnMemory, trackConversionEvent } from "../lib/plusConversion";


export function WelcomeBackReveal({ messages }) {
  const [payload, setPayload] = useState(null);
  const [shown, setShown] = useState(false);

  const userTurns = messages.filter((m) => m.role === "user").length;

  useEffect(() => {
    if (shown) return;
    // Founder rule A — only render AFTER the player has sent their
    // first message this session. Conversation-first, not dashboard.
    if (userTurns < 1) return;
    let cancelled = false;
    (async () => {
      const r = await fetchReturnMemory();
      if (cancelled) return;
      if (r && r.available) {
        setPayload(r);
        setShown(true);
        trackConversionEvent({
          kind: "plus_conversion_return_memory_reveal_shown",
          platform: "web",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [userTurns, shown]);

  if (!payload?.available) return null;
  const m = payload.mission || {};
  return (
    <div className="rounded-xl border border-emerald-800/50 bg-emerald-950/30 p-4 my-2"
          data-testid="welcome-back-reveal">
      <div className="text-xs font-medium text-emerald-300 mb-1">Welcome back</div>
      {m.focus && (
        <p className="text-sm text-neutral-200 mb-1">
          Last time we were working on: <span className="text-white">{m.focus}</span>
        </p>
      )}
      {m.test && (
        <p className="text-sm text-neutral-400">
          Your experiment: <span className="text-neutral-200">{m.test}</span>
        </p>
      )}
      {payload.commitment && (
        <p className="text-xs text-neutral-500 mt-1 italic">{payload.commitment}</p>
      )}
      <p className="text-sm text-emerald-300 mt-2">How did it go?</p>
    </div>
  );
}


export function MyCricketCounters() {
  const [counters, setCounters] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchCounters();
      if (!cancelled) setCounters(r);
    })();
    return () => { cancelled = true; };
  }, []);
  if (!counters) return null;
  const rows = [
    ["sessions understood", counters.sessions_understood],
    ["active goals",         counters.active_goals],
    ["active experiments",   counters.active_experiments],
    ["possible patterns",    counters.possible_patterns],
    ["confirmed patterns",   counters.confirmed_patterns],
    ["saved performances",   counters.saved_performances],
    ["linked videos",        counters.linked_videos],
  ].filter(([, v]) => typeof v === "number");   // never fabricate
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-3 space-y-1"
          data-testid="my-cricket-counters">
      <div className="text-xs text-neutral-500 mb-1">My Cricket</div>
      {rows.map(([label, val]) => (
        <div key={label} className="flex justify-between text-xs">
          <span className="text-neutral-400">{label}</span>
          <span className="text-white font-medium">{val}</span>
        </div>
      ))}
      <p className="text-[10px] text-neutral-600 pt-1">
        These are the actual signals Gully has picked up so far.
      </p>
    </div>
  );
}


/**
 * Wire on top of the existing billing_cta attachment. When the user
 * clicks the CTA, we fire `plus_conversion_cta_clicked` with the same
 * `conversion_context_id` so the funnel is traceable through purchase.
 */
export function trackCtaClick(attachment) {
  if (!attachment?.conversion_context_id) return;
  trackConversionEvent({
    kind: "plus_conversion_cta_clicked",
    conversion_context_id: attachment.conversion_context_id,
    trigger_reason: attachment.intent,
    cta_variant: attachment.cta_variant,
    platform: "web",
  });
  // Iter XX.224 — iOS Meta App Event (initiatedCheckout equivalent).
  // Consent-gated inside the dispatcher; no-op on web / Android.
  try {
    import("../lib/iosMetaEvents").then(m => {
      m.logPlusCtaTapped({
        trigger: attachment.intent,
        cta_variant: attachment.cta_variant,
        conversion_context_id: attachment.conversion_context_id,
      });
    }).catch(() => {});
  } catch {}
}
